// json-tree.component.ts
import { Component, Input } from '@angular/core';
import { BOTONES } from '../../../../componentes/botones';
import { TooltipDirective } from '../../../../auxiliares/tooltip/tooltip.directive';

@Component({
  selector: 'app-json-tree',
  standalone: true,
  imports: [BOTONES, TooltipDirective],
  templateUrl: './json-tree.component.html',
  styleUrls: ['./json-tree.component.scss'],
})
export class JsonTreeComponent {
  @Input() data: any;
  @Input() path = 'root';
  @Input() expanded: { [key: string]: boolean } = {};

  toggle(path: string): void {
    this.expanded[path] = !this.expanded[path];
  }

  isObject(val: any): boolean {
    return val && typeof val === 'object' && !Array.isArray(val);
  }

  isArray(val: any): boolean {
    return Array.isArray(val);
  }

  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  isExpanded(path: string): boolean {
    return !!this.expanded[path];
  }

  /** Objeto o array: los dos se recorren con `objectKeys` (en el array, las
   *  claves son los índices). */
  esContenedor(val: any): boolean {
    return !!val && typeof val === 'object';
  }

  /**
   * Un nodo se despliega si es un contenedor con contenido. Antes sólo se
   * desplegaban los objetos: un array caía en la rama de valor simple y se
   * imprimía interpolado, o sea "[object Object]".
   */
  esExpandible(val: any): boolean {
    return this.esContenedor(val) && this.objectKeys(val).length > 0;
  }

  isEmptyObject(val: any): boolean {
    return this.esContenedor(val) && this.objectKeys(val).length === 0;
  }

  /** `[]` o `{}` según el contenedor vacío que sea. */
  etiquetaVacio(val: any): string {
    return Array.isArray(val) ? '[]' : '{}';
  }

  /** Índices entre corchetes cuando el padre es un array, para que se lea como
   *  JSON y no como una clave más. */
  etiquetaClave(key: string): string {
    return Array.isArray(this.data) ? `[${key}]` : key;
  }

  /** Cantidad de elementos de un array, al lado de la clave. El espaciado lo
   *  pone el layout, no el texto. */
  cantidad(val: any): string {
    return Array.isArray(val) ? `(${val.length})` : '';
  }

  /** El botón de copiar todo va una sola vez, arriba: es un componente
   *  recursivo y antes se dibujaba uno por nivel anidado. */
  get esRaiz(): boolean {
    return this.path === 'root';
  }

  /** Clase del valor según su tipo, para colorearlo como en cualquier visor de
   *  JSON. */
  claseValor(val: any): string {
    if (val === null || val === undefined) return 'nulo';
    // Contenedor vacío: se muestra como `{}` o `[]`, en gris y no en color de
    // texto, que quedaría como si fuera un dato.
    if (this.esContenedor(val)) return 'nulo';
    if (typeof val === 'number') return 'numero';
    if (typeof val === 'boolean') return 'booleano';
    return 'texto';
  }

  /** `null` y `undefined` se escriben, no se muestran vacíos. */
  textoValor(val: any): string {
    if (val === null) return 'null';
    if (val === undefined) return 'undefined';
    return String(val);
  }

  public copyJson(): void {
    if (!this.data) return;

    const jsonString = JSON.stringify(this.data, null, 2);

    if (navigator.clipboard) {
      navigator.clipboard.writeText(jsonString).catch((err) => {
        console.error('Error al copiar JSON:', err);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = jsonString;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Error al copiar JSON (fallback):', err);
      }
      document.body.removeChild(textarea);
    }
  }

  public copyValue(value: any): void {
    const textToCopy =
      typeof value === 'object'
        ? JSON.stringify(value, null, 2)
        : String(value);

    if (navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy).catch((err) => {
        console.error('Error al copiar el valor:', err);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Error al copiar el valor (fallback):', err);
      }
      document.body.removeChild(textarea);
    }
  }
}
